/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 共有 AppShell の Header 右側に渡すアクション群（言語切替トグル / テーマ切替トグル）。i18n 依存をこのモジュールに閉じる。
 * @graph-connects none
 */
import { Globe, Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@cortex/web-design-system/hooks/useTheme';

/** @graph-connects none */
const THEME_STORAGE_KEY = 'theme';

/** @graph-connects none */
const LANGUAGES = [
  { code: 'ja', label: '日本語' },
  { code: 'en', label: 'English' },
  { code: 'vi', label: 'Tiếng Việt' },
] as const;

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 共有 AppShell の Header に渡す言語切替トグル。i18next の changeLanguage で ja/en/vi を巡回切替する。
 * @graph-connects none
 */
export function LanguageToggle() {
  const { i18n, t } = useTranslation();
  const currentLangCode = i18n.language.split('-')[0];
  const currentIndex = LANGUAGES.findIndex((l) => l.code === currentLangCode);
  const nextLang = LANGUAGES[(currentIndex + 1) % LANGUAGES.length];

  return (
    <button
      type="button"
      onClick={() => {
        void i18n.changeLanguage(nextLang.code);
      }}
      className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] p-2 text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-accent-primary)] hover:text-[var(--color-text-primary)]"
      aria-label={t('app.switchLanguage', { lang: nextLang.label })}
      title={LANGUAGES.find((l) => l.code === currentLangCode)?.label ?? LANGUAGES[0].label}
    >
      <Globe className="h-4 w-4" aria-hidden="true" />
    </button>
  );
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 共有 AppShell の Header に渡すテーマ切り替えトグル。共有 useTheme で dark/light を切り替える。
 * @graph-connects none
 */
export function ThemeToggle() {
  const { resolved, setTheme } = useTheme(THEME_STORAGE_KEY);
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={() => {
        setTheme(resolved === 'dark' ? 'light' : 'dark');
      }}
      className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] p-2 text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-accent-primary)] hover:text-[var(--color-text-primary)]"
      aria-label={resolved === 'dark' ? t('theme.switchToLight') : t('theme.switchToDark')}
    >
      {resolved === 'dark' ? (
        <Sun className="h-4 w-4" aria-hidden="true" />
      ) : (
        <Moon className="h-4 w-4" aria-hidden="true" />
      )}
    </button>
  );
}
